/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { MeRerm } from './me-rerm'
import { RoleReq } from './role-req'
import { Role } from './role'
import { LoadRoleQuery } from './load-role-query'
import { QueryRolesQuery } from './query-roles-query'

const buildConfig = createConfigBuilder('growp')

export const permApi = {
  queryPermsConfig(options?: AxiosRequestExtraConfig): AxiosRequestConfig {
    return buildConfig(options, 'get', '/perm/perms', {})
  },

  /**
   * @summary 正式用户-管理员-查询权限列表
   * @param [options]
   * @return AxiosResponsePromise<string[]>
   */
  queryPerms(
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<string[]> {
    return axios(permApi.queryPermsConfig(options)) as any
  },

  useQueryPerms(queryOptions?: QueryOptions): UseQueryApiReturns<string[]> {
    return useQueryApi<string[]>(
      permApi.queryPerms,
      'Perm.queryPerms',
      queryOptions
    )
  },

  queryMePermsConfig(options?: AxiosRequestExtraConfig): AxiosRequestConfig {
    return buildConfig(options, 'get', '/perm/perms/me', {})
  },

  /**
   * @summary 前台用户/后台用户/大后台用户-查询本人权限列表
   * @param [options]
   * @return AxiosResponsePromise<MeRerm>
   */
  queryMePerms(
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<MeRerm> {
    return axios(permApi.queryMePermsConfig(options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new MeRerm(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useQueryMePerms(queryOptions?: QueryOptions): UseQueryApiReturns<MeRerm> {
    return useQueryApi<MeRerm>(
      permApi.queryMePerms,
      'Perm.queryMePerms',
      queryOptions
    )
  },

  createRoleConfig(
    roleReq: RoleReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'post', '/perm/roles', {}, null, roleReq, {
      format: 'json',
      isArray: false,
      defaults: {
        name: (v: any) => v || '',
        description: (v: any) => v || '',
        perms: (v: any) => v || [],
        uids: (v: any) => v || [],
      },
    })
  },

  /**
   * @summary 正式用户-管理员-创建角色
   * @param roleReq
   * @param [options]
   * @return AxiosResponsePromise<Role>
   */
  createRole(
    roleReq: RoleReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Role> {
    return axios(permApi.createRoleConfig(roleReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Role(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useCreateRole(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: RoleReq) => permApi.createRole(payload),
      mutationOptions
    )
  },

  updateRoleConfig(
    id: number,
    roleReq: RoleReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/perm/roles/{id}',
      {
        id,
      },
      null,
      roleReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          name: (v: any) => v || '',
          description: (v: any) => v || '',
          perms: (v: any) => v || [],
          uids: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 正式用户-管理员-编辑角色
   * @param id
   * @param roleReq
   * @param [options]
   * @return AxiosResponsePromise<Role>
   */
  updateRole(
    id: number,
    roleReq: RoleReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Role> {
    return axios(permApi.updateRoleConfig(id, roleReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Role(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useUpdateRole(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: RoleReq }) =>
        permApi.updateRole(id, payload),
      mutationOptions
    )
  },

  deleteRoleConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'delete', '/perm/roles/{id}', {
      id,
    })
  },

  /**
   * @summary 正式用户-管理员-删除角色
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteRole(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(permApi.deleteRoleConfig(id, options)) as any
  },

  useDeleteRole(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => permApi.deleteRole(id),
      mutationOptions
    )
  },

  loadRoleConfig(
    id: number,
    loadRoleQuery?: LoadRoleQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/perm/roles/{id}',
      {
        id,
      },
      loadRoleQuery
    )
  },

  /**
   * @summary 正式用户-管理员-查询单个角色
   * @param id
   * @param [loadRoleQuery]
   * @param [loadRoleQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<Role>
   */
  loadRole<TQuery extends LoadRoleQuery>(
    id: number,
    loadRoleQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Role, TQuery['expand']>> {
    return axios(permApi.loadRoleConfig(id, loadRoleQuery, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Role(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useLoadRole<TQuery extends LoadRoleQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Role, TQuery['expand']>> {
    return useQueryApi<Expand<Role, TQuery['expand']>>(
      permApi.loadRole,
      'Perm.loadRole',
      queryOptions,
      id,
      query
    )
  },

  queryRolesConfig(
    queryRolesQuery?: QueryRolesQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/role/roles', {}, queryRolesQuery)
  },

  /**
   * @summary 正式用户-管理员-查询角色列表
   * @param [queryRolesQuery]
   * @param [queryRolesQuery.name]
   * @param [queryRolesQuery.nameLike]
   * @param [queryRolesQuery.expand]
   * @param [queryRolesQuery.paginated] 是否分页，默认 1
   * @param [queryRolesQuery.page] 显示的数据页，默认 1
   * @param [queryRolesQuery.perPage] 每页显示数量，默认 20
   * @param [options]
   * @return AxiosResponsePromise<Role[]>
   */
  queryRoles<TQuery extends QueryRolesQuery>(
    queryRolesQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Role, TQuery['expand']>[]> {
    return axios(permApi.queryRolesConfig(queryRolesQuery, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? _data.map((d: any) => new Role(d)) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useQueryRoles<TQuery extends QueryRolesQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Role, TQuery['expand']>[]> {
    return useQueryApi<Expand<Role, TQuery['expand']>[]>(
      permApi.queryRoles,
      'Perm.queryRoles',
      queryOptions,
      query
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(permApi).forEach((api) => {
  // @ts-ignore
  permApi[api]._name_ = `Perm.${api}`
})
